import { useMutation } from "react-query";
import Toast from "react-native-toast-message";
import axios from "axios";
import { Platform, Share } from "react-native";
import openhouseAPI from "../services/openhouse";
import { File, Paths } from "expo-file-system";

interface DownloadSignInSheetParams {
  qrCodeUrl?: string;
  userUuid?: string;
  streetAddress?: string;
  displayAgentLabel?: string;
  agentBrivityUuid?: string;
  onBeforeShare?: () => void;
}

const downloadSignInSheet = async (params: DownloadSignInSheetParams = {}) => {
  const {
    qrCodeUrl,
    userUuid,
    streetAddress,
    displayAgentLabel,
    agentBrivityUuid,
    onBeforeShare,
  } = params;

  const fileName = `${streetAddress}.pdf`;

  let displayAgentUuid;

  if (displayAgentLabel) {
    const hostsResponse = await openhouseAPI.get("reporting/openHouseHosts");

    displayAgentUuid = hostsResponse.data.hosts.find(
      (host: any) => host.value === agentBrivityUuid
    )?.meta?.user_uuid;
  }

  if (Platform.OS === "web") {
    const response = await openhouseAPI.put("reporting/openHouseSignInSheet", {
      openHouseSignInData: {
        qr_code_url: qrCodeUrl,
        user_uuid: userUuid,
        display_agent_uuid: displayAgentUuid,
        address: streetAddress,
      },
    });

    const pdfUrl = response.data.downloadPdf;

    const pdfResponse = await axios.get(pdfUrl, {
      responseType: "blob",
    });

    const blob = pdfResponse.data;
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(downloadUrl);

    if (onBeforeShare) {
      onBeforeShare();
    }

    return { success: true, fileName, platform: "web" };
  } else {
    const response = await openhouseAPI.put("reporting/openHouseSignInSheet", {
      openHouseSignInData: {
        qr_code_url: qrCodeUrl,
        user_uuid: userUuid,
        display_agent_uuid: displayAgentUuid,
        address: streetAddress,
      },
    });

    const pdfUrl = response.data.downloadPdf;

    try {
      const response = await fetch(pdfUrl);
      const arrayBuffer = await response.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const file = new File(Paths.cache, fileName);
      await file.write(uint8Array, {});

      const fileUri = file.uri;

      const fileExists = file.exists;

      if (!fileExists) {
        throw new Error("File was not saved properly");
      }

      if (onBeforeShare) {
        onBeforeShare();
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      try {
        const shareResult = await Share.share(
          {
            url: fileUri,
            title: "Sign-In Sheet",
          },
          {
            dialogTitle: "Share Sign-In Sheet",
          }
        );

        if (shareResult.action === Share.dismissedAction) {
          const permanentFile = new File(Paths.document, fileName);
          await permanentFile.write(uint8Array, {});
        } else if (shareResult.action === Share.sharedAction) {
        }
      } catch (shareError) {
        console.error("Error during share:", shareError);
        throw shareError;
      }

      return {
        success: true,
        fileName,
        platform: "mobile-share",
        action: "shared",
      };
    } catch (error) {
      console.error("Error downloading or sharing file:", error);
      if (onBeforeShare) {
        onBeforeShare();
      }
      throw error;
    }
  }
};

export const useDownloadSignIn = () => {
  return useMutation(downloadSignInSheet, {
    onSuccess: (data) => {
      const title = "Sign-In Sheet Downloaded";

      Toast.show({
        type: "success",
        text1: title,
      });
    },
    onError: (error: any) => {
      console.error("Failed to download sign-in sheet:", error);
      Toast.show({
        type: "error",
        text1: "Download Failed",
        text2:
          "There was an error downloading the sign-in sheet. Please try again.",
      });
    },
  });
};
